import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { OtpVerifyInterface } from '@core/models/authentication.model';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { SESSION_KEYS } from '@feature/auth/constants/auth.constant';
import { OtpVerificationType } from '@feature/auth/enums/auth.enum';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { ROUTES } from '@sharedModule/constants/route.constants';
import { showValidationMsg } from '@sharedModule/utils/form.utils';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-verify-otp',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputModule],
  templateUrl: './verify-otp.component.html',
  styleUrl: './verify-otp.component.scss',
})
export class VerifyOtpComponent implements OnInit, OnDestroy {
  public readonly attributeConstant = ATTRIBUTES_CONSTANT;
  public verifyOTP!: FormGroup;

  private readonly destroy$ = new Subject<void>();
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly messageHandlerService = inject(MessageHandlerService);
  private readonly authenticationService = inject(AuthenticationService);

  private readonly otpVerificationType: OtpVerificationType;
  private readonly userEmail: string;

  constructor() {
    const typeParam = this.route.snapshot.paramMap.get(
      'type'
    ) as OtpVerificationType;
    this.otpVerificationType = typeParam || OtpVerificationType.LOGIN;

    this.userEmail =
      this.sessionStorageService.get(SESSION_KEYS.USER_EMAIL) || '';
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onVerifyOTPSubmit(): void {
    if (!this.verifyOTP.valid) {
      showValidationMsg(this.verifyOTP);
      return;
    }

    const verificationHandlers: Record<OtpVerificationType, () => void> = {
      [OtpVerificationType.LOGIN]: (): void => this.verifyLoginOTP(),
      [OtpVerificationType.FORGOT_PASSWORD]: (): void =>
        this.verifyForgotPasswordOTP(),
    };

    const handler = verificationHandlers[this.otpVerificationType];
    if (handler) {
      handler();
    }
  }

  private initializeComponent(): void {
    if (!this.userEmail) {
      this.router.navigate([ROUTES.LOGIN]);
      return;
    }
    this.initializeForm();
  }

  private initializeForm(): void {
    this.verifyOTP = this.formBuilder.group({
      [this.attributeConstant.verification_code.key]: [
        '',
        [Validators.required],
      ],
    });
  }

  private createOTPPayload(): OtpVerifyInterface {
    return {
      email: this.userEmail,
      ...this.verifyOTP.value,
    };
  }

  private verifyLoginOTP(): void {
    this.authenticationService
      .verifyLoginOTP(this.createOTPPayload())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.success) {
            this.sessionStorageService.removeBy(SESSION_KEYS.USER_EMAIL);
            this.authenticationService.onLoggedIn(response.response);
          }
        },
      });
  }

  private verifyForgotPasswordOTP(): void {
    this.authenticationService
      .verifyForgotPasswordOTP(this.createOTPPayload())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.success) {
            this.sessionStorageService.set(
              SESSION_KEYS.RESET_PASSWORD_TOKEN,
              response.response.token
            );
            this.messageHandlerService.show(response.message);
            this.router.navigate([ROUTES.RESET_PASSWORD]);
          }
        },
      });
  }
}
